import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, List, ListItem,
  ListItemButton, ListItemText, CircularProgress, Chip,
  InputAdornment, Divider, Alert
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SearchIcon from '@mui/icons-material/Search';
import { cardManagementService } from '../../../../../services/cardManagementService';

interface CardResult {
  id: number;
  cardNumber: string;
  cardUID?: string;
  cardTypeName?: string;
  cardTypeColor?: string;
  accountName: string;
  /** Sahiplik türüne göre çözülmüş ad: cari adı ya da personel adı. */
  ownerName?: string;
  ownerType?: 'ACCOUNT' | 'PERSONNEL';
  isActive: boolean;
  totalBalance: number;
  activeSession?: {
    id: number;
    status: string;
    accountName: string;
    ownerName?: string;
    ownerType?: 'ACCOUNT' | 'PERSONNEL';
    walletBalances: Array<{ walletId: number; walletName: string; currency: string; balance: number }>;
    totalBalance: number;
  };
  wallets: Array<{ walletId: number; walletName: string; currency: string; balance: number }>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCardSelected: (card: CardResult) => void;
  companyId: string;
}

const PrepaidCardSearchModal: React.FC<Props> = ({ open, onClose, onCardSelected, companyId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<CardResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setResults([]);
      setError('');
      setSearched(false);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 200);
    }
  }, [open]);

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim() || !companyId) return;
    setLoading(true);
    setError('');
    setSearched(true);

    try {
      let card: CardResult | null = null;

      if (searchTerm.trim().length <= 20 && !searchTerm.includes(' ')) {
        try {
          const uidRes = await cardManagementService.getCardByUID(companyId, searchTerm.trim());
          if (uidRes.data?.data) {
            card = uidRes.data.data;
          }
        } catch {
          // UID ile bulunamadi, search ile dene
        }
      }

      if (!card) {
        const cardsRes = await cardManagementService.getCards(companyId, {
          search: searchTerm.trim(),
          pageSize: 10,
          sessionStatus: 'ACTIVE'
        });
        const cards = cardsRes.data?.data || [];
        if (cards.length === 1) {
          const fullRes = await cardManagementService.getCardById(cards[0].id, companyId);
          card = fullRes.data?.data || null;
        } else if (cards.length > 1) {
          const fullCards: CardResult[] = [];
          for (const c of cards) {
            try {
              const fullRes = await cardManagementService.getCardById(c.id, companyId);
              if (fullRes.data?.data) fullCards.push(fullRes.data.data);
            } catch { /* skip */ }
          }
          setResults(fullCards);
          setLoading(false);
          return;
        }
      }

      if (card) {
        if (!card.activeSession || card.activeSession.status !== 'ACTIVE') {
          setError('Bu kartın aktif oturumu yok. Önce PYK ekranından oturum açınız.');
          setResults([card]);
        } else {
          onCardSelected(card);
        }
      } else {
        setError('Kart bulunamadı');
        setResults([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kart aranırken hata oluştu');
    }
    setLoading(false);
  }, [searchTerm, companyId, onCardSelected]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelectCard = (card: CardResult) => {
    if (!card.activeSession || card.activeSession.status !== 'ACTIVE') {
      setError('Bu kartın aktif oturumu yok. Önce PYK ekranından oturum açınız.');
      return;
    }
    onCardSelected(card);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, minHeight: '40vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditCardIcon color="primary" />
          <Typography variant="h6">Kart Seç</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 2, pt: '8px !important' }}>
        <TextField
          fullWidth
          placeholder="Kart numarası veya UID girin..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
          size="small"
          autoComplete="off"
          sx={{ mb: 2 }}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={handleSearch}
          disabled={loading || !searchTerm.trim()}
          sx={{ mb: 2, height: 44 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'ARA'}
        </Button>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {results.length > 0 && (
          <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: '40vh' }}>
            <List sx={{ p: 0 }}>
              {results.map((card) => (
                <React.Fragment key={card.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleSelectCard(card)}
                      disabled={!card.activeSession || card.activeSession.status !== 'ACTIVE'}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontWeight={600}>{card.cardNumber}</Typography>
                            {card.cardTypeName && (
                              <Chip
                                label={card.cardTypeName}
                                size="small"
                                sx={{
                                  bgcolor: card.cardTypeColor || '#1976d2',
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            )}
                            {card.activeSession?.status === 'ACTIVE' ? (
                              <Chip label="Oturum Açık" size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />
                            ) : (
                              <Chip label="Oturum Kapalı" size="small" color="default" sx={{ fontSize: '0.7rem', height: 20 }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                            {card.cardUID && (
                              <Typography variant="caption" component="span" color="text.secondary">
                                UID: {card.cardUID}
                              </Typography>
                            )}
                            {card.activeSession && (
                              /* Sahip cari ya da personel olabilir; ownerName backend'de türe göre çözülür. */
                              <Typography variant="caption" component="span" color="text.secondary">
                                {card.ownerType === 'PERSONNEL' ? 'Personel' : 'Cari'}:{' '}
                                {card.activeSession.ownerName || card.activeSession.accountName || '—'}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{ fontWeight: 'bold', color: (card.activeSession?.totalBalance || 0) > 0 ? 'success.main' : 'text.secondary' }}
                            >
                              Bakiye: {formatCurrency(card.activeSession?.totalBalance || 0)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {searched && results.length === 0 && !error && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">Sonuç bulunamadı</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button fullWidth variant="outlined" onClick={onClose}>
          İptal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrepaidCardSearchModal;
